import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

function PrivacySettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'private'
  });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [unblockingId, setUnblockingId] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchPrivacySettings();
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/blocks/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setBlockedUsers(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch blocked users:', err);
    }
  };

  const handleUnblock = async (blockedUserId) => {
    setUnblockingId(blockedUserId);
    try {
      const response = await fetch(`${API_BASE_URL}/users/blocks/${blockedUserId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to unblock user');
      }
      setBlockedUsers(blockedUsers.filter(b => b.blocked_user_id !== blockedUserId));
      setSuccess('User unblocked.');
    } catch (err) {
      setError(err.message);
    } finally {
      setUnblockingId(null);
    }
  };

  const fetchPrivacySettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/privacy`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch privacy settings');
      }

      const data = await response.json();
      setPrivacySettings({
        profile_visibility: data.profile_visibility
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/users/social/privacy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          profile_visibility: privacySettings.profile_visibility
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update privacy settings');
      }

      setSuccess('Privacy settings updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <p className="mt-2">Loading privacy settings...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Form onSubmit={handleSave}>
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Profile Visibility</h5>
            <p className="text-muted mb-3">
              Control who can see your profile, reviews, favorites, and other content.
            </p>

            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                id="privacy-private"
                name="profile_visibility"
                label={
                  <div>
                    <strong>Private</strong>
                    <div className="text-muted small">
                      Only you can see your profile and content. Other users won't be able to view your information.
                    </div>
                  </div>
                }
                value="private"
                checked={privacySettings.profile_visibility === 'private'}
                onChange={(e) => setPrivacySettings({ profile_visibility: 'private' })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                id="privacy-public"
                name="profile_visibility"
                label={
                  <div>
                    <strong>Public</strong>
                    <div className="text-muted small">
                      Anyone can see your profile, reviews, favorites, and other content. Great for sharing your food discoveries!
                    </div>
                  </div>
                }
                value="public"
                checked={privacySettings.profile_visibility === 'public'}
                onChange={(e) => setPrivacySettings({ profile_visibility: 'public' })}
              />
            </Form.Group>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            {privacySettings.profile_visibility === 'private' ? (
              <span>🔒 Your profile is currently private</span>
            ) : (
              <span>🌐 Your profile is currently public</span>
            )}
          </div>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Privacy Settings'}
          </Button>
        </div>
      </Form>

      <Card className="mt-4">
        <Card.Body>
          <h5 className="mb-3">Blocked Users</h5>
          <p className="text-muted mb-3">
            You won't see reviews or content from blocked users. Block someone from their profile page.
          </p>
          {blockedUsers.length === 0 ? (
            <p className="text-muted mb-0">You haven't blocked anyone.</p>
          ) : (
            blockedUsers.map((block) => (
              <div key={block.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                <span>
                  {block.blocked_user_display_name || block.blocked_user_email || `User #${block.blocked_user_id}`}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleUnblock(block.blocked_user_id)}
                  disabled={unblockingId === block.blocked_user_id}
                >
                  {unblockingId === block.blocked_user_id ? 'Unblocking...' : 'Unblock'}
                </Button>
              </div>
            ))
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default PrivacySettings;
